/**
 * Quiz API utility functions
 * Centralized functions for interacting with the WordPress Quiz API plugin
 */

// WordPress REST base for quiz routes
// Example: NEXT_PUBLIC_BASE_URL=https://site.figmawya.com/
const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL + 'api/figmawya/v1';

/**
 * Get authentication headers for API requests (optional for quiz API)
 */
const getAuthHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Generate or retrieve a session ID for the quiz attempt
 * @returns {string} Session ID
 */
export const getOrCreateSessionId = () => {
  if (typeof window === 'undefined') return '';

  const storageKey = 'figmawya_quiz_session_id';
  let sessionId = sessionStorage.getItem(storageKey);

  if (!sessionId) {
    // Generate a UUID-like session ID
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
};

/**
 * Clear the session ID (useful when starting a new quiz)
 */
export const clearSessionId = () => {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('figmawya_quiz_session_id');
  }
};

/**
 * Get list of quizzes
 * @param {Object} params - Query parameters
 * @param {number} params.per_page - Number of quizzes per page (default: 50)
 * @param {number} params.page - Page number (default: 1)
 * @param {string} params.search - Search term (optional)
 * @returns {Promise<Array>} Array of quiz objects with id, title, excerpt, featuredImage, totalQuestions, createdAt
 */
export const getQuizzesList = async (params = {}) => {
  try {
    const { per_page = 50, page = 1, search = '' } = params;
    let url = `${BASE_URL}/quizzes?per_page=${per_page}&page=${page}`;

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching quizzes list:', error);
    throw error;
  }
};

/**
 * Get random questions for a quiz
 * @param {number} quizId - The quiz post ID
 * @param {number} count - Number of questions to retrieve (0 lets backend use q_count)
 * @returns {Promise<Object>} Response data containing quizId, title, and questions array
 */
export const getQuizQuestions = async (quizId, count = 0) => {
  try {
    const normalizedCount = Math.max(0, parseInt(count, 10) || 0);
    const url = `${BASE_URL}/quizzes/random?quiz=${quizId}&count=${normalizedCount}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching quiz questions:', error);
    throw error;
  }
};

/**
 * Submit quiz attempt with answers
 * @param {number} quizId - The quiz post ID
 * @param {string} sessionId - Session identifier for the attempt
 * @param {Array} answers - Array of answer objects
 * @param {Array<string>} servedQuestionUids - UIDs of questions shown to user
 * @returns {Promise<Object>} Response data containing attemptId, scores, and status
 */
export const submitQuizAttempt = async (quizId, sessionId, answers, servedQuestionUids = []) => {
  try {
    // Validate inputs
    if (!quizId || quizId <= 0) {
      throw new Error('معرف الاختبار غير صالح');
    }
    if (!sessionId || sessionId.trim() === '') {
      throw new Error('معرف الجلسة غير صالح');
    }
    if (!Array.isArray(answers) || answers.length === 0) {
      throw new Error('لا توجد إجابات للإرسال');
    }

    const payload = {
      quiz: quizId,
      sessionId: sessionId,
      answers: answers,
      servedQuestionUids: Array.isArray(servedQuestionUids) ? servedQuestionUids : [],
    };

    console.log('Submitting quiz attempt payload:', JSON.stringify(payload, null, 2));

    const response = await fetch(`${BASE_URL}/quizzes/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });

    let responseData;
    try {
      responseData = await response.json();
    } catch (parseError) {
      console.error('Failed to parse response as JSON:', parseError);
      const textResponse = await response.text();
      console.error('Response text:', textResponse);
      throw new Error(`فشل في معالجة الاستجابة من الخادم: ${response.status} ${response.statusText}`);
    }

    console.log('Submit response status:', response.status);
    console.log('Submit response data:', responseData);

    if (!response.ok) {
      const errorMessage = responseData.error || responseData.message || `HTTP ${response.status}: ${response.statusText}`;
      console.error('Submit error details:', {
        status: response.status,
        statusText: response.statusText,
        error: errorMessage,
        responseData,
        payload: payload
      });

      // Provide more specific error messages
      if (response.status === 400) {
        throw new Error(errorMessage || 'بيانات غير صحيحة. يرجى التحقق من الإجابات والمحاولة مرة أخرى.');
      } else if (response.status === 404) {
        throw new Error('الاختبار غير موجود');
      } else if (response.status === 429) {
        throw new Error(errorMessage || 'لقد استنفدت محاولاتك المسموحة خلال آخر 7 أيام.');
      } else if (response.status === 500) {
        throw new Error(errorMessage || 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.');
      } else {
        throw new Error(errorMessage);
      }
    }

    return responseData;
  } catch (error) {
    console.error('Error submitting quiz attempt:', error);
    throw error;
  }
};

/**
 * Format answer for submission based on question type
 * @param {Object} question - Question object with uid and type
 * @param {*} answerValue - The answer value(s) from user
 * @returns {Object} Formatted answer object for API
 */
export const formatAnswerForSubmission = (question, answerValue) => {
  const baseAnswer = {
    uid: question.uid,
    type: question.type,
  };

  switch (question.type) {
    case 'single_choice':
      return {
        ...baseAnswer,
        value: answerValue, // Should be the option key (e.g., "A", "B", "C", "D")
      };

    case 'multiple_choice':
      return {
        ...baseAnswer,
        values: Array.isArray(answerValue) ? answerValue : [answerValue], // Array of option keys
      };

    case 'true_false':
      return {
        ...baseAnswer,
        value: Boolean(answerValue), // true or false
      };

    case 'paragraph':
      return {
        ...baseAnswer,
        text: String(answerValue || ''), // Free text answer
      };

    default:
      console.warn(`Unknown question type: ${question.type}`);
      return baseAnswer;
  }
};

/**
 * Get attempt counts for a quiz (rolling 7-day window vs all-time).
 * @param {number} quizId  - The quiz post ID
 * @param {number} userId  - The user ID
 * @returns {Promise<{ userId: number, quizId: number|null, attemptsCount: number, allTimeAttemptsCount?: number, trialsWindowDays?: number, highestGrade?: number }>}
 */
export const getAttemptsCount = async (quizId, userId) => {
  try {
    const url = `${BASE_URL}/quizzes/attempts-count?quiz=${quizId}&userId=${userId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching attempts count:', error);
    throw error;
  }
};

/**
 * Get attempts summary for multiple quizzes in one request.
 * @param {number[]} quizIds
 * @param {number | null} userId
 * @returns {Promise<{ userId: number, quizIds: number[], summaries: Record<string, { attemptsCount: number, allTimeAttemptsCount?: number, trialsWindowDays?: number, highestGrade: number, isRamadan: boolean, passingThreshold: number, isPassed: boolean }>, missingQuizIds: number[] }>}
 */
export const getAttemptsSummary = async (quizIds, userId = null) => {
  const ids = Array.isArray(quizIds)
    ? Array.from(new Set(quizIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)))
    : [];

  if (ids.length === 0) {
    throw new Error('No valid quiz IDs provided');
  }

  try {
    const params = new URLSearchParams();
    params.set('quizIds', ids.join(','));
    if (userId != null && Number.isInteger(Number(userId)) && Number(userId) > 0) {
      params.set('userId', String(Number(userId)));
    }

    const response = await fetch(`${BASE_URL}/quizzes/attempts-summary?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching attempts summary:', error);
    throw error;
  }
};

/**
 * List the current user's quiz attempts (scores only; no answers). Requires Bearer token.
 * @param {{ page?: number, per_page?: number, status?: 'all' | 'passed' | 'failed' }} params
 * @returns {Promise<{ userId: number, items: Array<{ attemptId, quizId, quizTitle, percentageObjective, scoreObjective, objectiveMaxScore, pendingManualReview, passingThreshold, isRamadanQuiz, passed, completedAt }>, page: number, perPage: number, total: number, totalPages: number, status: string }>}
 */
export const getMyQuizAttempts = async (params = {}) => {
  const page = Math.max(1, parseInt(params.page, 10) || 1);
  const per_page = Math.min(50, Math.max(1, parseInt(params.per_page, 10) || 20));
  const status = ['all', 'passed', 'failed'].includes(params.status) ? params.status : 'all';

  const qs = new URLSearchParams({
    page: String(page),
    per_page: String(per_page),
    status,
  });

  const response = await fetch(`${BASE_URL}/quizzes/my-attempts?${qs.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (response.status === 401) {
    const err = new Error(data.error || 'يجب تسجيل الدخول لعرض محاولاتك');
    err.code = 'not_authenticated';
    err.status = 401;
    throw err;
  }

  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return data;
};

/**
 * Get attempts count with retries for transient failures.
 * Returns a typed status object so callers can keep deterministic UI state.
 * @param {number} quizId
 * @param {number} userId
 * @param {{ retries?: number, retryDelayMs?: number }} options
 * @returns {Promise<{ ok: boolean, data: any | null, error: string | null }>}
 */
export const getAttemptsCountWithRetry = async (quizId, userId, options = {}) => {
  const retries = Number.isInteger(options.retries) ? options.retries : 2;
  const retryDelayMs = Number.isInteger(options.retryDelayMs) ? options.retryDelayMs : 250;

  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const data = await getAttemptsCount(quizId, userId);
      return { ok: true, data, error: null };
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await sleep(retryDelayMs * (attempt + 1));
      }
    }
  }

  return {
    ok: false,
    data: null,
    error: lastError?.message || 'Failed to fetch attempts count',
  };
};

/**
 * Get leaderboard for a quiz scope (e.g. ramadan).
 * @param {{ type?: string, limit?: number }} params
 * @returns {Promise<{ type: string, totalQuizzesInScope: number, items: Array<{ userId, name, avatar, userRole, totalAttempts, quizzesCount, quizzesPassed, correctAttempts, averageCompletionPct, rank }> }>}
 */
export const getLeaderboard = async (params = {}) => {
  const { type = 'ramadan', limit = 50 } = params;
  try {
    const url = `${BASE_URL}/quizzes/leaderboard?type=${encodeURIComponent(type)}&limit=${Math.max(1, Math.min(Number(limit) || 50, 100))}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
};

// Export all functions
export default {
  getQuizzesList,
  getQuizQuestions,
  submitQuizAttempt,
  getOrCreateSessionId,
  clearSessionId,
  formatAnswerForSubmission,
  getAttemptsCount,
  getAttemptsSummary,
  getMyQuizAttempts,
  getAttemptsCountWithRetry,
  getLeaderboard,
};
